import { Button, Input } from '@assembly-js/design-system';
import { Popover } from '@mui/material';
import { Editor } from '@tiptap/core';
import { Formik } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { URLValidationRegex } from 'src/constants';
import { UrlUtils } from 'src/utils';

const linkSchema = z
  .object({
    linkUrl: z.string().regex(URLValidationRegex, 'Please enter a valid URL'),
  })
  .partial();

const LinkForm = ({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose: () => void;
}) => {
  const previousUrl: string | undefined = editor.getAttributes('link').href;

  return (
    <Formik
      initialValues={{ linkUrl: previousUrl ?? '' }}
      onSubmit={({ linkUrl }, { resetForm }) => {
        if (linkUrl === '') {
          // unset link
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
          // set link
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: UrlUtils.GetFullUrl(linkUrl) })
            .run();
        }
        resetForm();
        onClose();
      }}
      validationSchema={toFormikValidationSchema(linkSchema)}
      validateOnBlur
      validateOnChange
    >
      {({ errors, handleBlur, handleChange, submitForm, touched, values }) => {
        return (
          <div className="flex flex-col gap-4">
            <Input
              autoFocus
              placeholder="Link"
              name="linkUrl"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.linkUrl}
              error={
                touched.linkUrl && errors.linkUrl !== undefined
                  ? errors.linkUrl
                  : undefined
              }
            />
            <div className="flex flex-row-reverse gap-4 w-">
              <Button label="Add" onClick={submitForm} />
              <Button label="Cancel" onClick={onClose} variant="secondary" />
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export const LinkButton = ({
  onClick,
}: {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <Button
      label="Link"
      onClick={onClick}
      prefixIcon="Link"
      variant="secondary"
      className="h-10 rounded-none [&>p]:text-[#90959d]"
    />
  );
};

export const LinkPopover = ({
  editor,
  onClose,
  anchorEl,
}: {
  editor: Editor;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}) => {
  return (
    <Popover
      open={anchorEl !== null}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: -3,
        horizontal: 'right',
      }}
      onClose={onClose}
      classes={{
        paper:
          'border border-solid border-secondary-hover shadow-dropdown-menu p-3 w-64',
      }}
    >
      <LinkForm editor={editor} onClose={onClose} />
    </Popover>
  );
};
